import React from "react";
import { formatInstantDay } from "../../lib/calendar-date";

/**
 * Rental maintenance work orders: the work-order shape, its normalizer and its
 * skins, shared by the register (/rentals/maintenance) and the work-order page
 * (/rentals/maintenance/:id). Moved out of MaintenanceWorkOrders.tsx unchanged
 * when the split list / detail pane became two pages (#815).
 */

export interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}

export interface Part {
  name?: string | null;
  qty?: number | string | null;
  cost?: number | string | null;
}

export type WorkOrderStatus = "open" | "in_progress" | "pending_approval" | "approved" | "cancelled";

export interface WorkOrder {
  id: string;
  wo_number: string;
  asset_id: string;
  asset_name: string;
  asset_tag: string;
  /** As the API sent it. A value outside the enum below, or none at all,
   *  renders the unknown badge rather than a status it may not have. */
  status: WorkOrderStatus | (string & {}) | null;
  /** As the API sent it; absent is shown as a dash, never as a guessed Medium. */
  priority: string | null;
  description: string;
  assigned_to: string;
  opened_date: string;
  checklist: ChecklistItem[];
  parts: Part[];
  parts_cost?: number;
  labour_cost?: number;
  third_party_cost?: number;
  total_cost?: number;
  cost_notes?: string;
}

/**
 * What `GET /surface-rentals/maintenance/work-orders` (and `/:id`) actually
 * returns: `maintenance_work_orders.*` plus `asset_name`, `asset_number`,
 * `asset_type` and `assigned_to_name` from its joins.
 *
 * The `WorkOrder` interface above is the SCREEN's shape and almost none of its
 * field names are the API's. `wo_number`, `opened_date`, `checklist` and
 * `parts` are all spelled differently on the wire, and `assigned_to` is a UUID
 * there and a person's name here. `status_history` is not a column, not a join
 * and not an endpoint — nothing anywhere produces it.
 */
export interface WorkOrderApiRow {
  id: string;
  asset_id: string;
  work_order_number?: string | null;
  asset_name?: string | null;
  asset_number?: string | null;
  status?: string | null;
  priority?: string | null;
  description?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  opened_at?: string | null;
  checklist_json?: unknown;
  parts_json?: unknown;
  parts_cost?: string | number | null;
  labour_cost?: string | number | null;
  third_party_cost?: string | number | null;
  cost_notes?: string | null;
}

/** Absent renders as an em dash, never as a blank or a zero. */
export const DASH = "—";

/**
 * NUMERIC comes back from pg as a STRING. `parseFloat(x) || 0` is the trap
 * CLAUDE.md names — it turns NULL into a confident $0.00 — so an absent cost
 * stays `undefined` and the caller decides how to render it.
 */
export function numOrUndefined(v: string | number | null | undefined): number | undefined {
  if (v === null || v === undefined || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** `timeZone` is the tenant's: `opened_at` is an instant, shown as the tenant's day. */
export function normalizeWorkOrder(row: WorkOrderApiRow, timeZone: string | null): WorkOrder {
  const partsCost = numOrUndefined(row.parts_cost);
  const labourCost = numOrUndefined(row.labour_cost);
  const thirdPartyCost = numOrUndefined(row.third_party_cost);

  return {
    id: row.id,
    wo_number: row.work_order_number ?? DASH,
    asset_id: row.asset_id,
    asset_name: row.asset_name ?? DASH,
    asset_tag: row.asset_number ?? DASH,
    // Not `?? "open"`: that read an absent status as a confident Open, which
    // is the claim the unknown badge below exists to avoid making.
    status: row.status ?? null,
    // Not `?? "medium"`, for the same reason.
    priority: row.priority ?? null,
    description: row.description ?? DASH,
    assigned_to: row.assigned_to_name ?? DASH,
    // `opened_at` is a timestamptz — an instant, so it converts to local time
    // rather than going through the calendar-day helper, which reads the UTC
    // prefix and reports the wrong day for anything stamped after 17:00 MST.
    opened_date: formatInstantDay(row.opened_at, timeZone) ?? DASH,
    checklist: Array.isArray(row.checklist_json) ? (row.checklist_json as ChecklistItem[]) : [],
    parts: Array.isArray(row.parts_json) ? (row.parts_json as Part[]) : [],
    parts_cost: partsCost,
    labour_cost: labourCost,
    third_party_cost: thirdPartyCost,
    // Not a stored column; the three components ARE returned, so this is the
    // only place the total exists. ALL OR NOTHING: it used to add `?? 0` for a
    // missing component, which printed a confident total short by whatever the
    // server did not send. Any component absent and the total is a dash.
    total_cost:
      partsCost === undefined || labourCost === undefined || thirdPartyCost === undefined
        ? undefined
        : partsCost + labourCost + thirdPartyCost,
    cost_notes: row.cost_notes ?? undefined,
  };
}

/** Priority as a small fill dot. Exhaustive over the CHECK; absent or unknown draws no dot. */
export const PRIORITY: Record<string, { label: string; dot: string }> = {
  critical: { label: "Critical", dot: "bg-fi-signal-red" },
  high: { label: "High", dot: "bg-fi-signal-amber" },
  medium: { label: "Medium", dot: "bg-fi-signal-slate" },
  low: { label: "Low", dot: "bg-fi-signal-slate" },
};

/** The priority's label, or a dash for an absent or unknown one. Own-property lookup, as woSkin. */
export function priorityLabel(priority: string | null): string {
  return priority != null && Object.prototype.hasOwnProperty.call(PRIORITY, priority) ? PRIORITY[priority].label : DASH;
}

export type Skin = { label: string; fill: string; rail: string };

/**
 * Work order status, exhaustive over `maintenance_work_orders_status_check`.
 * Five values over five hues, but two share amber on purpose: In Progress and
 * Pending are both work in flight that still needs someone (the crew, then the
 * approver), and the label tells them apart. Cancelled is slate — no hue claims
 * a cancelled order is anything but closed. Purple is not in the palette, so
 * Pending's old purple wash is gone.
 */
export const WO_STATUS: Record<string, Skin> = {
  open: { label: "Open", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue" },
  in_progress: { label: "In Progress", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber" },
  pending_approval: { label: "Pending", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber" },
  approved: { label: "Approved", fill: "bg-fi-signal-green", rail: "border-l-fi-green" },
  cancelled: { label: "Cancelled", fill: "bg-fi-signal-slate", rail: "border-l-fi-steel" },
};

/**
 * A status the map does not know — added after this build, or absent. A slate
 * dash and a transparent rail, never a real status's label or colour.
 *
 * Own-property lookup, not `map[status]`: the maps are object literals, so a
 * status spelled like an Object.prototype member would otherwise return a
 * function rather than falling through.
 */
export const UNKNOWN: Skin = { label: DASH, fill: "bg-fi-signal-slate", rail: "border-l-transparent" };

export function woSkin(status: string | null | undefined): Skin {
  return status != null && Object.prototype.hasOwnProperty.call(WO_STATUS, status) ? WO_STATUS[status] : UNKNOWN;
}

export function StatusBadge({ skin }: { skin: Skin }) {
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}
